import React, { useEffect, useState, useCallback, useMemo } from 'react'
// import PropTypes from 'prop-types';
import { DisplayCard, AllPayments, userHasCardOnFile, getAwnPlusAppPrice, getAwnTeamsAppPrice, showAwnFreeTrial, isPlus, plusSubHasCode, subIsTeams, subIsMonthly, getAppSubscriptionId, getSubscriptionConfig, GiftCodeForm, triggerNativeIAP, subIsIap, createSubscriptionPriceGetter, doAppVersionRedirect } from './'
import { Loader } from '../../components'
import classnames from 'classnames'
import { path } from 'ramda'
import { appIsGteVersion, isAndroidApp, isApp, isIosApp } from '../../common/ambient'
import { billingFetch, billingSubscribe } from './billingAccess'
import { useAddUserAction } from '../social/redux/addUserAction'
import bindDeviceActions from '../device/redux/bindDeviceActions'

const isGiftCodeProcess = (codeValidity) => {
  if (isApp() && codeValidity !== true) {
    return false
  }
  return true
}

// Determines if we should use Stripe flow instead of native IAP
const shouldUseStripeFlow = (user, codeValidity, paymentMethod) => {
  // Always use Stripe for gift codes
  if (isGiftCodeProcess(codeValidity)) {
    return true
  }
  
  // If payment method is explicitly set to stripe
  if (paymentMethod === 'stripe') {
    return true
  }
  
  // If user has existing subscription and it's a Stripe subscription (not IAP)
  if (user.plus && !subIsIap(user.plus)) {
    return true
  }
  
  // Default to IAP for mobile apps if no other conditions met
  return false
}

function SubscribeProcess ({ user, subscription, children, device, actions, code = '', plusUserSwitch, setSubscription, redeemCode = false, onSubscriptionChange, useDiscountedPricing = false, forceIAP = false, paymentMethod = 'auto', startPolling }) {
  const [hasPaymentMethod, setHasPaymentMethod] = useState(!!userHasCardOnFile(user))
  const [loading, setLoading] = useState(false)
  const [_code, setCode] = useState(code)
  const [_plusUserSwitch, setPlusUserSwitch] = useState(plusUserSwitch)
  const [codeValidity, setCodeValidity] = useState(null)
  const [codeProductName, setCodeProductName] = useState(null)
  const [showCodeInput, setShowCodeInput] = useState(redeemCode || !!code)
  const { addUserAction } = useAddUserAction()
  const isPlusUser = isPlus(user)

  // Consolidated logic for determining payment flow
  const determineUseStripeFlow = useCallback(() => {
    // Force IAP if explicitly requested
    if (forceIAP) {
      return false
    }

    // Always use Stripe for discounted pricing
    if (useDiscountedPricing) {
      return true
    }

    // Use the existing shouldUseStripeFlow logic
    return shouldUseStripeFlow(user, codeValidity, paymentMethod)
  }, [user, codeValidity, paymentMethod, useDiscountedPricing, forceIAP])
  
  // Auto-show gift code input when redeemCode is true or code is provided
  useEffect(() => {
    if (redeemCode || code) {
      setShowCodeInput(true)
    }
  }, [redeemCode, code])
  useEffect(() => {
    if (device.appPurchaseFlowCanceled && loading) {
      setLoading(false)
      actions.setThing('appPurchaseFlowCanceled', false)
    }
  }, [device, loading, setLoading, actions])

  const annual = /yearly/.test(subscription)
  const isTeams = /for-teams/.test(subscription)
  const appSubscriptionId = getAppSubscriptionId(subscription)

  // Determine if this is a downgrade (from teams to individual or from annual to monthly)
  const isTierDowngrade = plusUserSwitch && user.plus && subIsTeams(user.plus) && !isTeams
  const isBillingDowngrade = plusUserSwitch && user.plus && !subIsMonthly(user.plus) && !annual
  const isDowngrade = isTierDowngrade || isBillingDowngrade
  const useIapFlow = useMemo(() =>
    (isAndroidApp() || isIosApp()) && !determineUseStripeFlow()
  , [determineUseStripeFlow])

  // btnBuyHandler ---------------------------------
  const btnBuyHandler = useCallback(async () => {
    setLoading(true)
    actions.setThing('appPurchaseFlowCanceled', false)
    if (typeof window.saveAppleSubscriptionPricingInfo === 'undefined') {
      window.saveAppleSubscriptionPricingInfo = (data) => {
        addUserAction({
          data,
          // we need "to" for the index
          to: {
            type: 'user',
            _id: user.info._id
          },
          type: 'iap-subscription'
        })
      }
    }
    const userId = path(['info', '_id'], user)
    // Use consolidated logic for determining payment flow
    const useStripeFlow = determineUseStripeFlow()

    // Native IAP flow
    if ((isIosApp() || isAndroidApp()) && !useStripeFlow) {
      if (doAppVersionRedirect(subscription)) {
        setLoading(true)
        actions.setThing('appPurchaseFlowCanceled', true)
        return
      }

      const success = triggerNativeIAP(subscription, userId)
      if (!success) {
        setLoading(false)
        // Could show error message or fallback to CC flow
        return
      }
    // stripe flow (not ios or android native app, or forced CC from app)
    } else {
      const billingSubOpts = {}
      let productName = subscription
      
      // If we have a valid code with a productName, use that instead
      if (codeValidity === true && codeProductName) {
        productName = codeProductName
      }
      
      if (useDiscountedPricing) {
        billingSubOpts.discountPercent = 20
      }
      
      // changing product name requires a PUT
      if (user.plus && user.plus.productName !== productName) {
        billingSubOpts.body = { new_product_name: productName }
        productName = user.plus.productName
        billingSubOpts.method = 'PUT'
      }
      if (codeValidity === true) {
        billingSubOpts.promoCode = _code
      }
      if (user.plus && codeValidity === true) {
        await billingFetch(`/subscriptions/apply-promo-code/?userId=${path(['info', '_id'], user)}&promoCode=${_code}`, { method: 'POST' })
      } else {
        if (isApp()) {
          billingSubOpts.headers = { 'x-platform': isIosApp() ? 'ios' : 'android' }
        }
        const result = await billingSubscribe(path(['info', '_id'], user), null, productName, billingSubOpts)
        // Only start refresh if subscription was successful
        if (!result.success) {
          setLoading(false)
          // Could add error handling here if needed
          return
        }
      }
      // Start polling for payment completion
      if (startPolling) {
        startPolling()
      }
    }
  }, [user, _code, subscription, codeValidity, codeProductName, actions, addUserAction, determineUseStripeFlow, useDiscountedPricing, startPolling])
  // btnBuyHandler ---------------------------------
  // STREAMLINED PROCESS REMOVED - all users now see the $0.00 step
  // const streamlinedCodeProcess = isPlusUser && !_plusUserSwitch && hasPaymentMethod
  const streamlinedCodeProcess = false // Disabled for now
  // useEffect(() => {
  //   console.log('SubscribeProcess streamlined check:', { streamlinedCodeProcess, codeValidity })
  //   if (streamlinedCodeProcess && codeValidity === true) {
  //     console.log('SubscribeProcess: Auto-calling btnBuyHandler due to streamlined process')
  //     btnBuyHandler()
  //   }
  // }, [codeValidity, streamlinedCodeProcess, btnBuyHandler])

  const handleCodeValidated = ({ code, productName, valid }) => {
    setCode(code)
    setCodeValidity(valid)
    setCodeProductName(productName)
    
    // Check if we need to switch subscription type based on code
    if (productName) {
      const currentIsTeams = /for-teams/.test(subscription)
      const codeIsTeams = /for-teams/.test(productName)
      
      if (currentIsTeams !== codeIsTeams && setSubscription) {
        // Switch to the appropriate subscription based on code's productName
        setSubscription(productName)
        
        // Notify parent component of the subscription change
        if (onSubscriptionChange) {
          onSubscriptionChange(productName)
        }
      }
    }
  }

  const subscriptionConfig = getSubscriptionConfig(subscription)
  const getPricing = createSubscriptionPriceGetter(device)
  let price = subscriptionConfig ? subscriptionConfig.price.split('/')[0] : '$0.00'

  const btnDisabled = useMemo(() => {
    let disabled = loading
    if (!useIapFlow) {
      disabled = disabled || !hasPaymentMethod
    }
    return disabled
  }, [loading, useIapFlow, hasPaymentMethod])

  if (isApp()) {
    // Show discounted price if using discounted CC pricing
    if (useDiscountedPricing) {
      const regularPrice = parseFloat(subscriptionConfig.price.replace(/[^0-9.]/g, ''))
      const discountedPrice = (regularPrice * 0.8).toFixed(2)
      price = `$${discountedPrice}`
    } else {
      const pricingInfo = getPricing(isTeams ? 'teams' : 'plus', annual, !isGiftCodeProcess(codeValidity))
      price = pricingInfo.price.split('/')[0] // Remove /month or /year
    }
  }
  let btnText = isDowngrade ? (isBillingDowngrade ? 'Puchase' : 'Downgrade') : 'Upgrade'
  let priceSubCopy = ''
  let codeValid = false
  let extraText = ''
  if (showAwnFreeTrial(user)) {
    priceSubCopy = 'First month free trial'
  }
  if (codeValidity === true) {
    btnText = 'Finish'
    price = '$0.00'
    
    // Set price copy based on the actual code's subscription type
    if (codeProductName) {
      const codeConfig = getSubscriptionConfig(codeProductName)
      if (codeConfig) {
        priceSubCopy = `Free year then ${codeConfig.price}`
      }
    } else {
      // Default to AWN+ annual price from config
      const defaultConfig = getSubscriptionConfig('awn-plus-account-yearly')
      if (defaultConfig) {
        priceSubCopy = `Free year then ${defaultConfig.price}`
      }
    }
    
    codeValid = true
    if (!hasPaymentMethod) {
      extraText = 'To activate your gift subscription, please register your card first.'
    }
  }

  let show = ''
  if (path(['info', '_id'], user)) {
    let codeShow = (
      <>
        {!showCodeInput ? (
          <p className='have-one'>
            <a onClick={() => setShowCodeInput(true)}>Have a gift code?</a>
          </p>
        ) : (
          <>
            <GiftCodeForm
              user={user}
              onCodeValidated={handleCodeValidated}
              initialCode={_code}
              disabled={loading}
            />
          </>
        )}
      </>
    )
    if (!annual) {
      codeShow = (
        <>
          <p>
            Do you have a gift code for a FREE AWN+ annual subscription?
          </p>
          <p>
            <a onClick={() => {
              setSubscription(subscription.replace('monthly', 'yearly'))
              setPlusUserSwitch(false)
            }}>Redeem gift code here</a>
          </p>
        </>
      )
    }
    if (plusSubHasCode(user)) {
      codeShow = null
    }
    show = (
      <>
        {!useIapFlow && (
          <>
            <DisplayCard />
            <AllPayments
              userId={path(['info', '_id'], user)}
              mode='file'
              onSuccess={() => setHasPaymentMethod(true)}
            />
          </>
        )}
        <div className='cart'>
          <div className='code'>
            {codeShow}
          </div>
          <div className='price'>
            <div>{price}/{annual ? 'year' : 'month'}</div>
            {priceSubCopy && <b>{priceSubCopy}</b>}
            {annual && !codeValid && !useDiscountedPricing && (() => {
              // Calculate monthly equivalent for annual subscriptions
              const annualConfig = getSubscriptionConfig(subscription)
              if (annualConfig) {
                const yearlyPrice = parseFloat(annualConfig.price.replace(/[^0-9.]/g, ''))
                const monthlyEquivalent = (yearlyPrice / 12).toFixed(2)
                return <p>-17% discount (${monthlyEquivalent}/month)</p>
              }
              return null
            })()}
          </div>
        </div>
        <div className='buyBtn'>
          <button
            className='btn btn-primary buy-plan'
            disabled={btnDisabled}
            onClick={btnBuyHandler}
          >
            {btnText}
          </button>
          {extraText && <p><b>{extraText}</b></p>}
          {loading ? <><br /><p>Please wait</p><Loader /></> : ''}
          {children}
        </div>
      </>
    )
  }
  return (
    <div className={classnames('subscribe-process', { plus: streamlinedCodeProcess })}>
      {show}
    </div>
  )
}

SubscribeProcess.propTypes = {}
SubscribeProcess.defaultProps = {}

SubscribeProcess.displayName = 'SubscribeProcess'

export default bindDeviceActions(SubscribeProcess)
